<template>
  <teleport to="body">
    <div v-if="modelValue" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <!-- Fond sombre -->
      <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity backdrop-blur-sm"></div>

      <!-- Contenu du modal -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
          <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <h3 id="modal-title" class="text-lg font-medium leading-6 text-gray-900 mb-4">
              {{ isEditing ? 'Modifier le Schéma' : 'Créer un nouveau Schéma' }}
            </h3>
            
            <!-- Formulaire -->
            <form @submit.prevent="saveSchema">
              <!-- Champ Type -->
              <div class="mb-4">
                <label for="schema-type" class="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select 
                  id="schema-type"
                  v-model="editableSchema.type" 
                  required
                  class="w-full p-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="" disabled>Sélectionnez un type</option>
                  <option value="Acquisition">Acquisition</option>
                  <option value="Processing">Processing</option>
                  <option value="Analyse">Analyse</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>

              <!-- Champ Appellation -->
              <div class="mb-4">
                <label for="schema-appellation" class="block text-sm font-medium text-gray-700 mb-1">Appellation</label>
                <input 
                  id="schema-appellation"
                  v-model="editableSchema.appellation" 
                  type="text" 
                  required
                  class="w-full p-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Nom du schéma"
                />
              </div>

              <!-- Champ Description -->
              <div class="mb-4">
                <label for="schema-description" class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea 
                  id="schema-description"
                  v-model="editableSchema.description" 
                  rows="3" 
                  class="w-full p-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Description détaillée du schéma"
                ></textarea>
              </div>
            </form>
          </div>
          
          <!-- Boutons d'action -->
          <div class="bg-gray-50 px-4 py-3 sm:flex sm:px-6">
                          <button 
                v-if="isEditing && canDelete"
                type="button"
                class="inline-flex justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:mr-auto disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="!canDelete"
                @click="confirmDelete"
              >
                Supprimer
              </button>
            <div class="sm:flex sm:flex-row-reverse">
              <button 
                type="button"
                class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed" 
                :disabled="isEditing ? !canEdit : !canCreate"
                @click="saveSchema"
              >
                Enregistrer
              </button>
              <button 
                type="button"
                class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto" 
                @click="closeModal"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
  <ModalAreYouSure 
    v-model="showDeleteConfirmation"
    title="Supprimer le schéma ?"
    message="Êtes-vous sûr de vouloir supprimer ce schéma ? Cette action ne peut pas être annulée."
    @confirm="deleteSchema"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useAdminPermissions } from '@/composables/useAdminPermissions';
import ModalAreYouSure from '../utils/modal_areyousure.vue';

// --- Props & Emits --- 
const props = defineProps({
  modelValue: { // Utilisé pour contrôler la visibilité (v-model)
    type: Boolean,
    required: true,
  },
  schemaData: { // Données du schéma pour l'édition
    type: Object,
    default: () => ({ uuid: null, appellation: '', type: '', description: '', section_list: [] }),
  },
  isEditing: { // Indique si c'est une création ou une édition
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'save', 'delete']);

// --- State --- 
const editableSchema = ref({ ...props.schemaData });
const showDeleteConfirmation = ref(false);
const { canCreate, canEdit, canDelete } = useAdminPermissions();

// --- Watcher pour mettre à jour les données internes quand les props changent ---
watch(() => props.schemaData, (newData) => {
  // Crée une copie profonde pour éviter les modifications directes de props
  editableSchema.value = JSON.parse(JSON.stringify(newData || { uuid: null, appellation: '', type: '', description: '', section_list: [] }));
  // S'assurer que le type est initialisé même si null/undefined dans les props
  if (!editableSchema.value.type) {
    editableSchema.value.type = '';
  }
}, { immediate: true, deep: true });

// --- Computed --- 
const isValid = computed(() => {
  return editableSchema.value.appellation?.trim() && editableSchema.value.type;
});

// --- Methods --- 
const closeModal = (): void => {
  emit('update:modelValue', false);
};

const saveSchema = (): void => {
  console.log('🎯 SchemaEditModal - saveSchema appelé:', {
    isValid: isValid.value,
    isEditing: props.isEditing,
    editableSchema: editableSchema.value
  });
  
  if (isValid.value) {
    if (props.isEditing) {
      // Pour la mise à jour, envoyer seulement les champs modifiables
      const dataToEmit = {
        appellation: editableSchema.value.appellation,
        type: editableSchema.value.type,
        description: editableSchema.value.description || '',
      };
      console.log('🔄 Émission save pour édition:', dataToEmit);
      emit('save', dataToEmit);
    } else {
      // Pour la création, utiliser le format SchemaCreate
      const dataToEmit = {
        appellation: editableSchema.value.appellation,
        type: editableSchema.value.type || '',
        description: editableSchema.value.description || '',
        section_list: [], // Liste vide par défaut, sera gérée après la création
      };
      console.log('🆕 Émission save pour création:', dataToEmit);
      emit('save', dataToEmit);
    }
    // Ne pas fermer le modal ici - laisser le parent le faire après succès
  } else {
    console.error("Formulaire invalide:", {
      appellation: editableSchema.value.appellation,
      type: editableSchema.value.type,
      isValid: isValid.value
    });
  }
};

const confirmDelete = (): void => {
  showDeleteConfirmation.value = true;
};

const deleteSchema = (): void => {
  if (props.isEditing && props.schemaData?.uuid) {
    emit('delete', props.schemaData.uuid);
  }
  showDeleteConfirmation.value = false;
  closeModal();
};

</script>

<style scoped>
/* Styles spécifiques au modal si nécessaire */
</style> 